import { useState, useEffect, useRef, useCallback } from 'react';
import { authHeaders } from '../utils/auth';
import { apiFetch } from '../utils/api';
import EnrollmentGallery from '../components/users/EnrollmentGallery';
import EnrollmentModal from '../components/users/EnrollmentModal';

const ROLES = [
  { value: 'artist', label: 'Artist' },
  { value: 'artistic_director', label: 'Artistic Director' },
];

const emptyForm = {
  fName: '',
  lName: '',
  email: '',
  password: '',
  role: 'artist',
};
const emptyEditForm = { fName: '', lName: '', email: '', role: 'artist' };
const emptyModalState = {
  running: false,
  done: false,
  total: 0,
  currentIndex: 0,
  currentItem: null,
  currentStage: '',
  results: [],
};

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [fetchState, setFetchState] = useState({
    status: 'loading',
    error: null,
  });
  const [roleFilter, setRoleFilter] = useState('');
  const [refresh, setRefresh] = useState(0);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState(emptyEditForm);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);

  const [deactivating, setDeactivating] = useState(null);

  // Enrollment state
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [newPhotos, setNewPhotos] = useState([]);
  const [removedIds, setRemovedIds] = useState(new Set());
  const [localDevWarning, setLocalDevWarning] = useState(false);
  const [enrollModal, setEnrollModal] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const cancelRef = useRef(false);

  useEffect(() => {
    setFetchState({ status: 'loading', error: null });
    const url = roleFilter ? `/api/users?role=${roleFilter}` : '/api/users';
    apiFetch(url, { headers: authHeaders() })
      .then(r => r.json())
      .then(data => {
        setUsers(Array.isArray(data) ? data : []);
        setFetchState({ status: 'idle', error: null });
      })
      .catch(err => {
        setFetchState({ status: 'error', error: err.message });
      });
  }, [roleFilter, refresh]);

  const resetEnrollment = () => {
    setExistingPhotos([]);
    setNewPhotos([]);
    setRemovedIds(new Set());
    setLocalDevWarning(false);
  };

  const handleCreate = async e => {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);
    try {
      const res = await apiFetch('/api/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) {
        setSaveError(data.message || 'Failed to create user');
        return;
      }
      const userId = data.data.id;
      if (newPhotos.length > 0) {
        await runEnrollment(userId);
      } else {
        setForm(emptyForm);
        setShowForm(false);
        resetEnrollment();
        setRefresh(n => n + 1);
      }
    } catch {
      setSaveError('Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (userId, userName) => {
    if (
      !window.confirm(
        `Deactivate ${userName}? They will no longer be able to log in.`
      )
    )
      return;
    setDeactivating(userId);
    try {
      const res = await apiFetch(`/api/users/${userId}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok) {
        const data = await res.json();
        alert(data.message || 'Failed to deactivate user');
        return;
      }
      setUsers(prev => prev.filter(u => u.id !== userId));
    } catch {
      alert('Failed to deactivate user');
    } finally {
      setDeactivating(null);
    }
  };

  const handleEdit = async user => {
    setEditingUser(user);
    setEditForm({
      fName: user.fName,
      lName: user.lName,
      email: user.email,
      role: user.role,
    });
    setEditError(null);
    setShowForm(false);
    setForm(emptyForm);
    resetEnrollment();

    try {
      const res = await apiFetch(`/api/users/${user.id}/enrollment`, {
        headers: authHeaders(),
      });
      if (res.status === 503) {
        setLocalDevWarning(true);
      } else if (res.ok) {
        const data = await res.json();
        setExistingPhotos(data);
      }
    } catch {
      // Non-fatal — gallery just stays empty
    }
  };

  const handleUpdate = async e => {
    e.preventDefault();
    setEditSaving(true);
    setEditError(null);
    try {
      const patchBody = editingUser.role === 'admin'
        ? { fName: editForm.fName, lName: editForm.lName, email: editForm.email }
        : editForm;
      const res = await apiFetch(`/api/users/${editingUser.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(patchBody),
      });
      const data = await res.json();
      if (!res.ok) {
        setEditError(data.message || 'Failed to update user');
        return;
      }
      setUsers(prev =>
        prev.map(u => (u.id === editingUser.id ? data.data : u))
      );

      if (newPhotos.length > 0 || removedIds.size > 0) {
        await runEnrollment(editingUser.id);
      } else {
        setEditingUser(null);
        setEditForm(emptyEditForm);
        resetEnrollment();
      }
    } catch {
      setEditError('Failed to update user');
    } finally {
      setEditSaving(false);
    }
  };

  const runEnrollment = useCallback(
    async userId => {
      cancelRef.current = false;

      const removalItems = [...removedIds].map(id => ({
        type: 'remove',
        id,
        photo: existingPhotos.find(p => p.id === id),
      }));
      const addItems = newPhotos.map(file => ({ type: 'add', file }));
      const items = [...removalItems, ...addItems];

      setEnrollModal({
        ...emptyModalState,
        running: true,
        total: items.length,
        results: [],
      });

      const addResult = (item, status, error) => {
        const filename =
          item.type === 'add' ? item.file.name : `Photo #${item.id}`;
        setEnrollModal(prev => ({
          ...prev,
          results: [
            ...prev.results,
            { filename, type: item.type, status, error },
          ],
        }));
      };

      for (let i = 0; i < items.length; i++) {
        if (cancelRef.current) {
          const remaining = items.slice(i);
          remaining.forEach(item => {
            const filename =
              item.type === 'add' ? item.file.name : `Photo #${item.id}`;
            setEnrollModal(prev => ({
              ...prev,
              results: [
                ...prev.results,
                { filename, type: item.type, status: 'skipped' },
              ],
            }));
          });
          break;
        }

        const item = items[i];
        const preview =
          item.type === 'add'
            ? URL.createObjectURL(item.file)
            : (item.photo?.thumbnailUrl ?? '');
        const filename =
          item.type === 'add' ? item.file.name : `Photo #${item.id}`;

        setEnrollModal(prev => ({
          ...prev,
          currentIndex: i,
          currentItem: { type: item.type, preview, filename },
          currentStage: item.type === 'remove' ? 'Removing...' : 'Uploading...',
        }));

        if (item.type === 'remove') {
          try {
            const res = await apiFetch(
              `/api/users/${userId}/enrollment/${item.id}`,
              {
                method: 'DELETE',
                headers: authHeaders(),
              }
            );
            if (!res.ok) throw new Error((await res.json()).message);
            addResult(item, 'success');
          } catch (err) {
            addResult(item, 'failed', err.message);
          }
          continue;
        }

        // type === 'add'
        let enrollmentPhotoId;
        try {
          const formData = new FormData();
          formData.append('photo', item.file);
          const uploadRes = await apiFetch(
            `/api/users/${userId}/enrollment/upload`,
            {
              method: 'POST',
              headers: authHeaders(),
              body: formData,
            }
          );
          if (!uploadRes.ok) throw new Error((await uploadRes.json()).message);
          ({ id: enrollmentPhotoId } = await uploadRes.json());

          setEnrollModal(prev => ({
            ...prev,
            currentStage: 'Indexing faces...',
          }));
          const indexRes = await apiFetch(
            `/api/users/${userId}/enrollment/${enrollmentPhotoId}/index`,
            {
              method: 'POST',
              headers: authHeaders(),
            }
          );
          if (!indexRes.ok) throw new Error((await indexRes.json()).message);
          const { faceId } = await indexRes.json();

          setEnrollModal(prev => ({ ...prev, currentStage: 'Associating...' }));
          const assocRes = await apiFetch(
            `/api/users/${userId}/enrollment/${enrollmentPhotoId}/associate`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...authHeaders() },
              body: JSON.stringify({ faceId }),
            }
          );
          if (!assocRes.ok) throw new Error((await assocRes.json()).message);

          addResult(item, 'success');
        } catch (err) {
          if (enrollmentPhotoId) {
            apiFetch(`/api/users/${userId}/enrollment/${enrollmentPhotoId}`, {
              method: 'DELETE',
              headers: authHeaders(),
            }).catch(() => {});
          }
          addResult(item, 'failed', err.message);
        }
      }

      setEnrollModal(prev => ({
        ...prev,
        running: false,
        done: true,
        currentStage: 'Done',
      }));
    },
    [existingPhotos, newPhotos, removedIds]
  );

  const handleModalClose = () => {
    setEnrollModal(null);
    setShowCancelConfirm(false);
    setForm(emptyForm);
    setShowForm(false);
    setEditingUser(null);
    setEditForm(emptyEditForm);
    resetEnrollment();
    setRefresh(n => n + 1);
  };

  const fmtRole = role => {
    if (role === 'artistic_director') return 'Artistic Staff';
    if (role === 'admin') return 'Admin';
    return 'Artist';
  };

  return (
    <div>
      <div className='user-mgmt-header'>
        <div className='user-filter-tabs'>
          {[
            { value: '', label: 'All' },
            { value: 'artist', label: 'Artists' },
            { value: 'artistic_director', label: 'Artistic Staff' },
            { value: 'admin', label: 'Admins' },
          ].map(f => (
            <button
              key={f.value}
              className={`user-filter-btn${roleFilter === f.value ? ' active' : ''}`}
              onClick={() => setRoleFilter(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <button
          className='user-add-btn'
          onClick={() => {
            setShowForm(true);
            setSaveError(null);
            setEditingUser(null);
            setEditForm(emptyEditForm);
            resetEnrollment();
          }}
        >
          + Add User
        </button>
      </div>

      {showForm && (
        <form className='user-form' onSubmit={handleCreate}>
          <h3 className='user-form-title'>New User</h3>
          <div className='user-form-row'>
            <div className='user-form-field'>
              <label>First Name</label>
              <input
                value={form.fName}
                onChange={e => setForm(f => ({ ...f, fName: e.target.value }))}
                required
              />
            </div>
            <div className='user-form-field'>
              <label>Last Name</label>
              <input
                value={form.lName}
                onChange={e => setForm(f => ({ ...f, lName: e.target.value }))}
                required
              />
            </div>
          </div>
          <div className='user-form-row'>
            <div className='user-form-field'>
              <label>Email</label>
              <input
                type='email'
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                required
              />
            </div>
            <div className='user-form-field'>
              <label>Temporary Password</label>
              <input
                type='text'
                value={form.password}
                onChange={e =>
                  setForm(f => ({ ...f, password: e.target.value }))
                }
                required
                minLength={8}
              />
            </div>
          </div>
          <div className='user-form-row'>
            <div className='user-form-field'>
              <label>Role</label>
              <select
                value={form.role}
                onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
              >
                {ROLES.map(r => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {form.role === 'artist' && (
            <EnrollmentGallery
              existingPhotos={existingPhotos}
              newPhotos={newPhotos}
              removedIds={removedIds}
              onAdd={files => setNewPhotos(prev => [...prev, ...files])}
              onRemoveNew={i =>
                setNewPhotos(prev => prev.filter((_, idx) => idx !== i))
              }
              onRemoveExisting={id =>
                setRemovedIds(prev => new Set([...prev, id]))
              }
              localDevWarning={localDevWarning}
            />
          )}
          {saveError && <p className='user-form-error'>{saveError}</p>}
          <div className='user-form-actions'>
            <button
              type='button'
              className='user-cancel-btn'
              onClick={() => {
                setShowForm(false);
                setForm(emptyForm);
                setSaveError(null);
                resetEnrollment();
              }}
            >
              Cancel
            </button>
            <button type='submit' className='user-submit-btn' disabled={saving}>
              {saving ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      )}

      {editingUser && (
        <form className='user-form' onSubmit={handleUpdate}>
          <h3 className='user-form-title'>Edit User</h3>
          <div className='user-form-row'>
            <div className='user-form-field'>
              <label>First Name</label>
              <input
                value={editForm.fName}
                onChange={e =>
                  setEditForm(f => ({ ...f, fName: e.target.value }))
                }
                required
              />
            </div>
            <div className='user-form-field'>
              <label>Last Name</label>
              <input
                value={editForm.lName}
                onChange={e =>
                  setEditForm(f => ({ ...f, lName: e.target.value }))
                }
                required
              />
            </div>
          </div>
          <div className='user-form-row'>
            <div className='user-form-field'>
              <label>Email</label>
              <input
                type='email'
                value={editForm.email}
                onChange={e =>
                  setEditForm(f => ({ ...f, email: e.target.value }))
                }
                required
              />
            </div>
            <div className='user-form-field'>
              <label>Role</label>
              {editingUser?.role === 'admin' ? (
                <input value='Admin' disabled readOnly />
              ) : (
                <select
                  value={editForm.role}
                  onChange={e =>
                    setEditForm(f => ({ ...f, role: e.target.value }))
                  }
                >
                  {ROLES.map(r => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
          {editForm.role === 'artist' && editingUser?.role !== 'admin' && (
            <EnrollmentGallery
              existingPhotos={existingPhotos}
              newPhotos={newPhotos}
              removedIds={removedIds}
              onAdd={files => setNewPhotos(prev => [...prev, ...files])}
              onRemoveNew={i =>
                setNewPhotos(prev => prev.filter((_, idx) => idx !== i))
              }
              onRemoveExisting={id =>
                setRemovedIds(prev => new Set([...prev, id]))
              }
              localDevWarning={localDevWarning}
            />
          )}
          {editError && <p className='user-form-error'>{editError}</p>}
          <div className='user-form-actions'>
            <button
              type='button'
              className='user-cancel-btn'
              onClick={() => {
                setEditingUser(null);
                setEditForm(emptyEditForm);
                setEditError(null);
                resetEnrollment();
              }}
            >
              Cancel
            </button>
            <button
              type='submit'
              className='user-submit-btn'
              disabled={editSaving}
            >
              {editSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      )}

      {fetchState.status === 'loading' && (
        <p className='user-loading'>Loading users...</p>
      )}
      {fetchState.status === 'error' && (
        <p className='user-error'>Failed to load users: {fetchState.error}</p>
      )}
      {fetchState.status === 'idle' && users.length === 0 && (
        <p className='user-empty'>No users found.</p>
      )}
      {fetchState.status === 'idle' && users.length > 0 && (
      <div className='table-responsive'>
        <table className='user-table'>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th className='enrolled-cell'>Auto Tag with AI</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id}>
                <td>
                  {u.fName} {u.lName}
                </td>
                <td>{u.email}</td>
                <td>
                  <span className={`role-badge role-${u.role}`}>
                    {fmtRole(u.role)}
                  </span>
                </td>
                <td className='enrolled-cell'>
                  {u.role === 'artist' &&
                    (u.isEnrolled ? (
                      <span className='enrolled-check'>&#10003;</span>
                    ) : (
                      <span className='enrolled-missing'>&#10007;</span>
                    ))}
                </td>
                <td>
                  <button
                    className='user-edit-btn'
                    onClick={() => handleEdit(u)}
                    disabled={deactivating === u.id}
                  >
                    Edit
                  </button>
                  <button
                    className='user-deactivate-btn'
                    onClick={() =>
                      handleDeactivate(u.id, `${u.fName} ${u.lName}`)
                    }
                    disabled={deactivating === u.id}
                  >
                    {deactivating === u.id ? 'Deactivating…' : 'Deactivate'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {enrollModal && (
        <EnrollmentModal
          state={enrollModal}
          showCancelConfirm={showCancelConfirm}
          onCancelClick={() => setShowCancelConfirm(true)}
          onCancelConfirm={() => {
            cancelRef.current = true;
            setShowCancelConfirm(false);
          }}
          onCancelDeny={() => setShowCancelConfirm(false)}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
}
